import React, { useState, useEffect } from 'react'
import Grid from '@mui/material/Grid'
import { useShowClaim } from '@/v2/hooks/insurance/useClaims'
import Patient from './subforms/Patient'
import Subscriber from './subforms/Subscriber'
import Providers from './subforms/Providers'
import Procedures from './subforms/Procedures'
import TreatmentForm from './subforms/Treatment'
import Payer from './subforms/Payer'
import Box from '@mui/material/Box'
import { useTheme } from '@mui/material/styles'
import Typography from '@/v2/components/common/Typography'
import Attachments from './subforms/Attachments'
import { useGetclaimAttachments } from '@/v2/hooks/insurance/claim-subforms/useClaimAttachments'
import { format, parseISO } from 'date-fns'
import { safeFormat } from '@/v2/utils/convert'
import Paper from '@mui/material/Paper'
import CodeBlock from '@/v2/components/common/CodeBlock'

const Edit = ({
  claim,
  insuranceId,
  error = {},
  onChange,
  patientId,
  viewOnly = false,
  scrollToHistory = false,
  onTriggerEdit,
  justCreated = false,
}) => {
  const { claimInfo, isLoading, refetch } = useShowClaim(insuranceId, claim?.id)
  const theme = useTheme()
  const [activities, setActivities] = useState([])
  const [values, setValues] = useState({ ...claim, insuranceId })
  const { claimAttachments } = useGetclaimAttachments(claim?.id)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [lastItemStartDate, setLastItemStartDate] = useState('')

  // Normalize an item's start date (Date or string) to yyyy-MM-dd so the
  // Treatment subform can default the appliance date to it.
  const handleItemSaved = startDate => {
    if (!startDate) return
    setLastItemStartDate(
      typeof startDate === 'string'
        ? startDate.slice(0, 10)
        : format(startDate, 'yyyy-MM-dd')
    )
  }

  if (!claim) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          No claim data provided
        </Typography>
      </Box>
    )
  }

  useEffect(() => {
    const fetchData = async () => {
      if (claim?.id && typeof refetch === 'function') {
        try {
          await refetch()
        } catch (error) {
          console.error('Error refetching claim data:', error)
        }
      }
    }
    fetchData()
  }, [claim?.id, refetch])

  useEffect(() => {
    if (claimInfo) {
      setValues({ ...claimInfo, insuranceId })
      setActivities(claimInfo.activities)
      setHasLoaded(true)
    }
  }, [claimInfo, insuranceId])

  useEffect(() => {
    if (claimAttachments) {
      setValues(prevValues => ({
        ...prevValues,
        attachments: claimAttachments,
      }))
    }
  }, [claimAttachments])

  useEffect(() => {
    if (scrollToHistory && !isLoading) {
      const timer = setTimeout(() => {
        const historySection = document.getElementById('history-section')
        if (historySection) {
          historySection.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 300)

      return () => clearTimeout(timer)
    }
  }, [scrollToHistory, isLoading])

  const handleChangeSubscriber = value => {
    const claimObject = {
      ...values,
      subscriber: { ...values.subscriber, ...value },
    }

    onChange(claimObject)
    setValues({ ...claimObject, insuranceId })

    if (hasLoaded && !viewOnly && onTriggerEdit) {
      onTriggerEdit(true)
    }
  }

  const handleTreatmentChange = value => {
    const claimObject = { ...values, treatment: value }
    onChange(claimObject)

    if (hasLoaded && !viewOnly && onTriggerEdit) {
      onTriggerEdit(true)
    }
  }

  // Function to group activities by date
  const groupActivitiesByDate = activities => {
    const grouped = activities.reduce((acc, activity) => {
      const formattedDate = safeFormat(
        parseISO(activity.createdAt),
        'MM/dd/yyyy'
      )
      if (!acc[formattedDate]) acc[formattedDate] = []
      acc[formattedDate].push(activity)
      return acc
    }, {})

    Object.values(grouped).forEach(list =>
      list.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    )

    const sortedByDate = Object.keys(grouped)
      .sort((a, b) => new Date(b) - new Date(a))
      .reduce((obj, date) => {
        obj[date] = grouped[date]
        return obj
      }, {})

    return sortedByDate
  }

  // Function to get the status color from theme
  const getStatusColor = status =>
    ({
      pending: theme.palette.grey.A700,
      validating: theme.palette.grey.A700,
      validated: theme.palette.warning.main,
      invalidated: theme.palette.error.main,
      submitting: theme.palette.warning.main,
      submitted: theme.palette.success.main,
      answered: theme.palette.success.main,
      approved: theme.palette.success.main,
      denied: theme.palette.error.main,
      cancelled: theme.palette.error.main,
      failed: theme.palette.error.main,
    }[status] || theme.palette.grey[500])

  const getLabel = status => {
    if (status === 'validated') return 'Ready to be Sent'
    if (status === 'submitted') return 'Sent'
    if (status === 'invalidated') return 'Invalid'
    if (status === 'answered') return 'Answered'
    return status
  }

  const extractClaimErrors = activity => {
    if (activity?.errors?.has_errors && activity?.errors?.all_errors) {
      const allErrors = activity.errors.all_errors
      return allErrors.length > 0 ? allErrors.join('\n') : null
    }

    return null
  }

  // Function to format update_status messages with colored statuses
  const formatStatusMessage = activity => {
    if (
      activity.action === 'update_status' &&
      activity.data?.from &&
      activity.data?.to
    ) {
      const fromLabel = getLabel(activity.data.from) // Get the formatted label as plain text
      const toLabel = getLabel(activity.data.to)

      return (
        <>
          Status updated from{' '}
          <span
            style={{
              fontWeight: 'bold',
              color: getStatusColor(activity.data.from),
            }}
          >
            {fromLabel}
          </span>{' '}
          to{' '}
          <span
            style={{
              fontWeight: 'bold',
              color: getStatusColor(activity.data.to),
            }}
          >
            {toLabel}
          </span>
          {activity.contextualNote && (
            <div style={{ marginTop: 4 }}>{activity.contextualNote}</div>
          )}
        </>
      )
    }
    return activity.message
  }

  const groupedActivities = groupActivitiesByDate(activities)

  // The "Date appliance placed" field defaults to the start date of the item
  // the user most recently added/edited, falling back to the first item's date.
  const firstItemStartDate =
    values?.items?.find(item => item?.startDate)?.startDate || ''
  const appliancePlacedDefault = lastItemStartDate || firstItemStartDate

  const extractDXCResponse = dxc => {
    const { data } = dxc
    const { response } = data
    const { claimStatus } = response
    const { categoryCodes } = claimStatus

    if (!categoryCodes || !categoryCodes.length)
      return 'No category codes found'

    return categoryCodes
      .map(
        ({ code, statusCode }) =>
          `X12 Codes - Category: ${code} | Status: ${statusCode}`
      )
      .join('\n')
  }

  if (isLoading) {
    return <div>Loading...</div>
  }

  return (
    <Grid container spacing={2}>
      {!isLoading && (
        <>
          <Payer
            viewOnly={viewOnly}
            values={values}
            insuranceId={insuranceId}
            error={error}
            claimId={claim.id}
            patientId={patientId}
          />

          <Patient
            viewOnly={viewOnly}
            values={values}
            insuranceId={insuranceId}
            error={error}
            claimId={claim.id}
            patientId={patientId}
          />

          <Subscriber
            isEdit
            viewOnly={viewOnly}
            values={values}
            insuranceId={insuranceId}
            error={error}
            onChange={handleChangeSubscriber}
            claimId={claim.id}
            patientId={patientId}
          />

          <Providers
            viewOnly={viewOnly}
            values={values}
            error={error}
            insuranceId={insuranceId}
            claimId={claim.id}
          />
          <Procedures
            viewOnly={viewOnly}
            values={values}
            error={error}
            insuranceId={insuranceId}
            claimId={claim.id}
            onItemSaved={handleItemSaved}
          />

          <TreatmentForm
            isEdit={true}
            viewOnly={viewOnly}
            values={values}
            defaultApplianceDate={appliancePlacedDefault}
            justCreated={justCreated}
            error={error}
            errorAffixe="treatment"
            onChange={handleTreatmentChange}
            insuranceId={insuranceId}
            claimId={claim.id}
            patientId={patientId}
          />

          <Attachments
            viewOnly={viewOnly}
            status={claim.status}
            values={values}
            error={error}
            insuranceId={insuranceId}
            claimId={claim.id}
          />

          {/* History Section */}
          <Grid item xs={12}>
            <Box
              id="history-section"
              sx={{
                marginTop: '20px',
                marginBottom: '12px',
                background: theme.palette.grey[100],
                padding: '16px',
                borderRadius: '4px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <Typography variant="h3" bold>
                History
              </Typography>
              {activities?.length > 0 ? (
                Object.keys(groupedActivities).map(date => (
                  <Box key={date} mt={3}>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {date}
                    </Typography>
                    {groupedActivities[date].map(activity => (
                      <Paper
                        key={activity.id}
                        elevation={1}
                        sx={{ padding: '12px', marginTop: '8px' }}
                      >
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1" sx={{ display: 'block' }}>
                            {formatStatusMessage(activity)}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            sx={{ display: 'block' }}
                          >
                            by {activity?.user?.fullName || 'System'} -{' '}
                            {safeFormat(
                              parseISO(activity?.createdAt),
                              'hh:mm a'
                            )}
                          </Typography>
                        </Box>

                        {activity.data?.to === 'invalidated' && (
                          <Box>
                            <Typography
                              variant="body2"
                              color="error"
                              fontWeight="bold"
                              sx={{ mb: 1 }}
                            >
                              Errors:
                            </Typography>
                            <CodeBlock>
                              {extractClaimErrors(activity)}
                            </CodeBlock>
                          </Box>
                        )}
                        {activity.data?.to === 'denied' && (
                          <>
                            <Box>
                              <Typography
                                variant="body2"
                                color="error"
                                fontWeight="bold"
                                sx={{ mb: 1 }}
                              >
                                Errors:
                              </Typography>
                              <CodeBlock>
                                {extractClaimErrors(activity)}
                              </CodeBlock>
                            </Box>
                            <Box mt={1}>
                              <a
                                href="https://x12.org/codes/claim-status-category-codes"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Category Translation
                              </a>{' '}
                              |{' '}
                              <a
                                href="https://x12.org/codes/claim-status-codes"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Status Translation
                              </a>
                            </Box>
                          </>
                        )}
                        {activity.data?.to === 'answered' && (
                          <>
                            <Box mt={1}>
                              <Typography
                                variant="body2"
                                color="error"
                                fontWeight="bold"
                              >
                                Response:
                              </Typography>
                              <CodeBlock>
                                {extractDXCResponse(activity.data)}
                              </CodeBlock>
                            </Box>
                            <Box mt={1}>
                              <a
                                href="https://x12.org/codes/claim-status-category-codes"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Category Translation
                              </a>{' '}
                              |{' '}
                              <a
                                href="https://x12.org/codes/claim-status-codes"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Status Translation
                              </a>
                            </Box>
                          </>
                        )}
                      </Paper>
                    ))}
                  </Box>
                ))
              ) : (
                <Typography variant="span">No history found</Typography>
              )}
            </Box>
          </Grid>
        </>
      )}
    </Grid>
  )
}

export default Edit
